import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { Meeting } from '@/types/soundstage';

// ==================== SCHEMA ====================

export interface CachedMeetings {
    meetings: Meeting[];
    hasMore: boolean;
}

interface MeetingsEntry extends CachedMeetings {
    cacheKey: string;
    updatedAt: number;
}

interface SingleMeetingEntry {
    meetingId: string;
    meeting: Meeting;
    updatedAt: number;
}

interface SoundstageDB extends DBSchema {
    meetings: {
        key: string;
        value: MeetingsEntry;
    };
    singleMeeting: {
        key: string;
        value: SingleMeetingEntry;
    };
}

// ==================== CONSTANTS ====================

const DB_NAME = 'probe-grid-soundstage';
const DB_VERSION = 1;
const MAX_CACHED_QUERIES = 30;
const MAX_CACHED_MEETINGS = 50;

// ==================== DB SINGLETON ====================

let dbPromise: Promise<IDBPDatabase<SoundstageDB>> | null = null;

function openSoundstageDB(): Promise<IDBPDatabase<SoundstageDB>> {
    if (!dbPromise) {
        dbPromise = openDB<SoundstageDB>(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('meetings')) {
                    db.createObjectStore('meetings', { keyPath: 'cacheKey' });
                }
                if (!db.objectStoreNames.contains('singleMeeting')) {
                    db.createObjectStore('singleMeeting', { keyPath: 'meetingId' });
                }
            },
        });
    }
    return dbPromise;
}

// ==================== MEETINGS LIST CACHE ====================

export async function getMeetingsCache(cacheKey: string): Promise<CachedMeetings | undefined> {
    try {
        const db = await openSoundstageDB();
        const entry = await db.get('meetings', cacheKey);
        if (!entry) return undefined;
        // Touch LRU
        db.put('meetings', { ...entry, updatedAt: Date.now() }).catch(() => {});
        return { meetings: entry.meetings, hasMore: entry.hasMore };
    } catch {
        return undefined;
    }
}

export async function setMeetingsCache(cacheKey: string, data: CachedMeetings): Promise<void> {
    try {
        const db = await openSoundstageDB();
        await db.put('meetings', {
            cacheKey,
            meetings: data.meetings,
            hasMore: data.hasMore,
            updatedAt: Date.now(),
        });
        await evictMeetingsCache(db);
    } catch {
        // Silently fail — cache is best-effort
    }
}

async function evictMeetingsCache(db: IDBPDatabase<SoundstageDB>): Promise<void> {
    const allKeys = await db.getAllKeys('meetings');
    if (allKeys.length <= MAX_CACHED_QUERIES) return;

    const entries = await db.getAll('meetings');
    entries.sort((a, b) => a.updatedAt - b.updatedAt);
    const toRemove = entries.slice(0, entries.length - MAX_CACHED_QUERIES);
    const tx = db.transaction('meetings', 'readwrite');
    for (const entry of toRemove) {
        tx.store.delete(entry.cacheKey);
    }
    await tx.done;
}

// ==================== SINGLE MEETING CACHE ====================

export async function getMeetingCache(meetingId: string): Promise<Meeting | undefined> {
    try {
        const db = await openSoundstageDB();
        const entry = await db.get('singleMeeting', meetingId);
        if (!entry) return undefined;
        db.put('singleMeeting', { ...entry, updatedAt: Date.now() }).catch(() => {});
        return entry.meeting;
    } catch {
        return undefined;
    }
}

export async function setMeetingCache(meetingId: string, meeting: Meeting): Promise<void> {
    try {
        const db = await openSoundstageDB();
        await db.put('singleMeeting', {
            meetingId,
            meeting,
            updatedAt: Date.now(),
        });
        await evictSingleMeetingCache(db);
    } catch {
        // Silently fail
    }
}

async function evictSingleMeetingCache(db: IDBPDatabase<SoundstageDB>): Promise<void> {
    const allKeys = await db.getAllKeys('singleMeeting');
    if (allKeys.length <= MAX_CACHED_MEETINGS) return;

    const entries = await db.getAll('singleMeeting');
    entries.sort((a, b) => a.updatedAt - b.updatedAt);
    const toRemove = entries.slice(0, entries.length - MAX_CACHED_MEETINGS);
    const tx = db.transaction('singleMeeting', 'readwrite');
    for (const entry of toRemove) {
        tx.store.delete(entry.meetingId);
    }
    await tx.done;
}

// ==================== PREFERENCES CACHE ====================

export async function getPreferencesCache(userId: string): Promise<any | undefined> {
    try {
        const db = await openSoundstageDB();
        const entry = await db.get('singleMeeting', `prefs:${userId}`);
        if (!entry) return undefined;
        return entry.meeting; // reuse singleMeeting store
    } catch {
        return undefined;
    }
}

export async function setPreferencesCache(userId: string, prefs: any): Promise<void> {
    try {
        const db = await openSoundstageDB();
        await db.put('singleMeeting', {
            meetingId: `prefs:${userId}`,
            meeting: prefs as any,
            updatedAt: Date.now(),
        });
    } catch {}
}

// ==================== USER SEARCH CACHE (in-memory) ====================
const searchCache = new Map<string, { results: any[]; timestamp: number }>();
const SEARCH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function getCachedSearchResults(orgId: string, query: string): any[] | undefined {
    const key = `${orgId}:${query?.toLowerCase() || ''}`;
    const cached = searchCache.get(key);
    if (!cached) return undefined;
    if (Date.now() - cached.timestamp > SEARCH_CACHE_TTL) {
        searchCache.delete(key);
        return undefined;
    }
    return cached.results;
}

export function setCachedSearchResults(orgId: string, query: string, results: any[]): void {
    const key = `${orgId}:${query?.toLowerCase() || ''}`;
    searchCache.set(key, { results, timestamp: Date.now() });
    // Evict old entries
    if (searchCache.size > 100) {
        const oldest = [...searchCache.entries()].sort((a, b) => a[1].timestamp - b[1].timestamp);
        for (let i = 0; i < 20; i++) searchCache.delete(oldest[i][0]);
    }
}

// ==================== CLEAR ====================

export async function clearSoundstageCache(): Promise<void> {
    try {
        const db = await openSoundstageDB();
        await db.clear('meetings');
        await db.clear('singleMeeting');
    } catch {
        // Silently fail
    }
}
